import { createSimpleStore, useSimpleStore } from '@passionware/simple-store';
import debounce from 'lodash/debounce';
import { OnlineStatusApi } from 'v5/api/online-status/online-status.api';
// import { internalInstance } from 'v5/i18n.internal';
import { ApiClientCommunicationEvent } from 'v5/platform/api/apiClientCommunicationEvent';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from '../ToastService/ToastService';
import { OnlineStatus, OnlineStatusService } from './OnlineStatusService';

const HEALTH_CHECK_DEBOUNCE_MS = 10_000;

function isTabVisible() {
  return (
    typeof document === 'undefined' || document.visibilityState === 'visible'
  );
}

function readBrowserOnline() {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export function createOnlineStatusService(
  config: WithServices<[WithToastService]> & {
    api: OnlineStatusApi;
    subscribeApiCommunication: (
      callback: (event: ApiClientCommunicationEvent) => void
    ) => () => void;
  }
): OnlineStatusServiceInstance {
  // const { toastService } = config.services;
  const browserOnlineStore = createSimpleStore(readBrowserOnline());
  const apiReachableStore = createSimpleStore(true);

  const resolveStatus = (): OnlineStatus => {
    if (
      !browserOnlineStore.getCurrentValue() ||
      !apiReachableStore.getCurrentValue()
    ) {
      return 'offline';
    }
    return 'online';
  };

  let lastNotifiedStatus: OnlineStatus = resolveStatus();

  const commitStatus = () => {
    const next = resolveStatus();
    if (next === lastNotifiedStatus) {
      return;
    }
    lastNotifiedStatus = next;

    // if (next === 'offline') {
    //   toastService.open({
    //     type: 'error',
    //     message: internalInstance.t('online_status.toast_offline', {
    //       ns: 'core'
    //     })
    //   });
    //   return;
    // }

    // toastService.open({
    //   type: 'success',
    //   message: internalInstance.t('online_status.toast_online', { ns: 'core' })
    // });
  };

  const setBrowserOnline = (online: boolean) => {
    browserOnlineStore.setNewValue(online);
    commitStatus();
  };

  const setApiReachable = (reachable: boolean) => {
    apiReachableStore.setNewValue(reachable);
    commitStatus();
  };

  let healthCheckAbortController: AbortController | null = null;

  const abortInFlightHealthCheck = () => {
    healthCheckAbortController?.abort();
    healthCheckAbortController = null;
  };

  const runHealthCheck = async () => {
    if (!isTabVisible()) {
      return;
    }

    abortInFlightHealthCheck();
    const abortController = new AbortController();
    healthCheckAbortController = abortController;

    try {
      await config.api.checkHealth(abortController.signal);
      if (!abortController.signal.aborted) {
        setApiReachable(true);
      }
    } catch {
      if (!abortController.signal.aborted) {
        setApiReachable(false);
      }
    } finally {
      if (healthCheckAbortController === abortController) {
        healthCheckAbortController = null;
      }
      scheduleHealthCheckWhileOffline();
    }
  };

  const debouncedHealthCheck = debounce(() => {
    void runHealthCheck();
  }, HEALTH_CHECK_DEBOUNCE_MS);

  const scheduleHealthCheck = () => {
    if (isTabVisible()) {
      debouncedHealthCheck();
    }
  };

  /** Keep probing while API is unreachable so we recover without a new user action. */
  const scheduleHealthCheckWhileOffline = () => {
    if (
      !isTabVisible() ||
      !browserOnlineStore.getCurrentValue() ||
      apiReachableStore.getCurrentValue()
    ) {
      return;
    }
    scheduleHealthCheck();
  };

  const runHealthCheckImmediately = () => {
    debouncedHealthCheck.cancel();
    void runHealthCheck();
  };

  const onApiCommunication = (event: ApiClientCommunicationEvent) => {
    if (event === 'communication_success') {
      setApiReachable(true);
      scheduleHealthCheck();
      return;
    }

    // A single failed request (e.g. CORS on one endpoint) is not proof the API is
    // unreachable — confirm with the dedicated health check before marking offline.
    if (isTabVisible()) {
      runHealthCheckImmediately();
    }
  };

  const onBrowserOnline = () => {
    setBrowserOnline(true);
    scheduleHealthCheck();
  };

  const onBrowserOffline = () => {
    setBrowserOnline(false);
  };

  const onVisibilityChange = () => {
    if (isTabVisible()) {
      scheduleHealthCheck();
    } else {
      debouncedHealthCheck.cancel();
      abortInFlightHealthCheck();
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onBrowserOnline);
    window.addEventListener('offline', onBrowserOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  const unsubscribeApiCommunication =
    config.subscribeApiCommunication(onApiCommunication);

  if (isTabVisible()) {
    scheduleHealthCheck();
  }

  const service: OnlineStatusService = {
    useStatus: () => {
      useSimpleStore(browserOnlineStore);
      useSimpleStore(apiReachableStore);
      return resolveStatus();
    },
    getStatus: resolveStatus
  };

  return Object.assign(service, {
    dispose: () => {
      debouncedHealthCheck.cancel();
      abortInFlightHealthCheck();
      unsubscribeApiCommunication();
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onBrowserOnline);
        window.removeEventListener('offline', onBrowserOffline);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    }
  });
}

export type OnlineStatusServiceInstance = OnlineStatusService & {
  dispose: () => void;
};
